/**
 * ScoresTable — R3 results surface (v38 requirements-analysis
 * `#aiReviewPreviewPanel`, lines 1757-1770): candidate rows × criterion
 * columns, the `ScoreLegend` footer, and two actions: **"Preview sample"** (above the
 * table — a cheap, non-persisting look at N random contacts that POPULATES the
 * table) and **"Review candidates"** (below — the primary CTA, score EVERY
 * contact and save them to a NEW import; the caller confirms + runs the full
 * review + auto-applies). Apply is **enabled only after a preview has been
 * run** for the current criteria; once the file exists, a static "Review file
 * created ✓" label replaces it (unless the criteria changed, which re-shows
 * Apply pending a fresh preview).
 *
 * TWO CLAIMS THAT USED TO BE IN THIS HEADER ARE GONE, and the deletion is the
 * point. It said the table renders "per-cell `1/2/3` `ScoreBadge` with a
 * rationale `Tooltip`, a Total column (always the simple average — criteria
 * are equal weight)". Both are now false: the rationale renders IN-CELL via
 * `ContactsTable`, and the total is `item.weighted_total` read from the API.
 *
 * The second half is the exact premise that caused this feature's first
 * blocker — it was read as an invariant, the mean was recomputed, and the
 * preview diverged from the file it was promising to match. ADR-0014 carries
 * the same correction for the same reason. Leaving it here would re-arm the
 * trap at the top of the very component the preview renders, which is where
 * the next person to add a weight control would read it. The rule: read the
 * total from the API, never recompute it.
 *
 * Two gates, not one (sub-task 03 packet 3.2, decision 1): `previewDisabled`
 * holds the things a RUN needs, `applyDisabled` the things the FILE needs. An
 * unnamed output file must not stop a recruiter previewing scores.
 *
 * Reused, not forked: `ScoreBadge` + `ScoreLegend` come from
 * `import-detail/` (presentational, no coupling — see the sub-task 04 PRD
 * "Score-cell reuse note"); `ProgressBar`/`Spinner`/`Button` come
 * from `@nu-assist/ui` (`Tooltip` went out with the private table).
 */
import type {
  AiReviewResult,
  AnnexColumn,
  ImportContactRow,
} from "@nu-assist/contracts";
import { Button, ProgressBar, Spinner } from "@nu-assist/ui";
import { ArrowDown, ArrowUp, ArrowUpDown, RefreshCw } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { ContactsTable } from "../import-detail/ContactsTable";
import { ScoreLegend } from "../import-detail/ScoreLegend";
import { aiReviewFailureText, aiReviewQueuedText, aiReviewWaitingText } from "./aiReviewFailure";
import { MAX_PREVIEW_IDS } from "./previewRows";
import { isRunInFlight } from "./useAiReviewRun";

export interface ScoresTableSort {
  key: string;
  dir: "asc" | "desc";
}

export interface ScoresTableProps {
  /** Latest run snapshot; `undefined` before the first run. */
  run: AiReviewResult | undefined;
  /** Mirrors `useAiReviewRun`'s `isPolling` — true while queued/in-flight. */
  isPolling: boolean;
  /** True when criteria changed since `run` last went terminal. */
  stale: boolean;
  /** Contacts in the selected import — drives the F2 empty state and the
   *  indeterminate-spinner candidate count. `null` means no import is
   *  selected yet (NOT the same as a selected-but-empty import, which is
   *  `0` and triggers the F2 "No candidates to score" empty state). */
  totalCandidates: number | null;
  /** Fires a PREVIEW run over `sampleSize` random contacts — the "Preview
   *  sample" button AND the failed banner's Retry both call this (the cheap,
   *  non-persisting look). */
  onPreview: () => void;
  /** How many contacts the preview button samples (button label + banner). */
  sampleSize: number;
  previewDisabled?: boolean;
  previewDisabledHint?: string;
  /** Fires the "Review candidates" compound action — score EVERY contact and
   *  save them to a NEW import in one click (the caller confirms first, runs
   *  the full review, then auto-applies on success). */
  onApplyAll: () => void;
  /** Gates the Apply CTA only. Everything `previewDisabled` covers plus the
   *  file-specific rules (an output name is required to create a file, but not
   *  to preview scores). */
  applyDisabled?: boolean;
  /** Explains an apply-ONLY gate. Deliberately not a mirror of
   *  `previewDisabledHint`: when a shared gate is unmet that hint already says
   *  so next to Preview, and repeating it under Apply is noise. */
  applyDisabledHint?: string;
  /** True from the moment Apply is confirmed until the derived file exists —
   *  spans the full run, the `202`, and the `applying` poll. Swaps the Apply
   *  label for a spinner + "Scoring…"/"Saving…". */
  applying: boolean;
  /** A PREVIEW create is in flight. Where the API scores previews inside the
   *  request (`AI_REVIEW_PREVIEW_INLINE`) that POST can take up to ~50 s, so
   *  the table shows the scoring state for `sampleSize` candidates meanwhile.
   *  Not `applying`: no file is involved, the Apply CTA just stays disabled. */
  previewPending?: boolean;
  /** The page has stopped waiting on this run (stall deadline, or the poll
   *  failing). The run may still be alive server-side — so this is NOT a
   *  failure — but nothing here is going to observe it finish, and the banner
   *  above owns the explanation.
   *
   *  It exists because this component keeps its OWN in-flight predicate: the
   *  page giving up has to reach that predicate, or the button keeps spinning
   *  "Saving…" beside a banner saying we stopped (`/pr-review` iter 2, C1-a). */
  abandoned?: boolean;
  /** The run's contacts, already joined with its scores by
   *  `previewRows.joinScores`. Empty until the contacts fetch lands, which is
   *  why `contactsPending` exists — see below. */
  rows?: ImportContactRow[];
  /** Annex descriptors built from the run's criteria (`criteriaToAnnexColumns`). */
  columns?: AnnexColumn[];
  /** Per-cell rationale lookup (`rationaleForRun`) — rendered IN-CELL by
   *  `ContactsTable`, exactly as on the Import Detail page. */
  rationaleFor?: (contactId: string, criterionKey: string) => string | undefined;
  /** Presigned avatar URLs for `rows` (`useResolveAvatars`). */
  resolvedAvatars?: ReadonlyMap<string, string>;
  /** The import under review — scopes `ContactsTable`'s width measurement. */
  importId?: string | null;
  /** True when the parent is a Company Map import, so the preview renders the
   *  same three match-reason columns its detail page will. */
  isBct?: boolean;
  /** True while the scored contacts are being fetched.
   *
   *  A run can be `succeeded` with its rows still in flight — the scores and
   *  the profile data come from two different endpoints. Without this the
   *  table would render "This run scored no candidates." for the moment
   *  between them, which is a lie about a run that just succeeded. */
  contactsPending?: boolean;
  /** True when the scored contacts could not be fetched.
   *
   *  Distinct from "the run scored nobody", which is what an empty table would
   *  otherwise say — and would be a lie about a run that succeeded. The scores
   *  exist; it is their profile half that did not arrive, so the message says
   *  that and offers the retry. */
  contactsFailed?: boolean;
  /** How many contacts the run scored, before the client truncated the id list
   *  to the endpoint's ceiling. When it exceeds the rows on screen the table
   *  says so — showing 25 of 1,240 without a word is the silent-wrong-answer
   *  shape this feature refused at the API. */
  scoredCount?: number;
  /** Refetch the scored contacts (the retry behind `contactsFailed`). */
  onRetryContacts?: () => void;
  /** Current sort state — drives sort toolbar `aria-pressed` state and glyph. */
  sort?: ScoresTableSort | null;
  /** Called with the key when a sort header is clicked — toggles dir if the
   *  key is already active, otherwise resets to ascending. */
  onSort?: (key: string) => void;
  /** Opens the person side panel from a row's avatar (`ContactsTable`'s
   *  `onOpenPerson`). Absent (flag off), the Name cell is plain. */
  onOpenPerson?: (row: ImportContactRow, handle: string) => void;
  /** The handle the panel is open on — that row is highlighted. */
  openPersonHandle?: string | null;
  /** The row the panel acts on, when the list holds that person twice. */
  openPersonRowId?: string | null;
}

function EmptyState() {
  return (
    <div
      data-testid="scores-empty"
      className="rounded-md border border-border bg-surface px-4 py-10 text-center text-body text-text-muted"
    >
      No candidates to score
    </div>
  );
}

function ContactsPending() {
  return (
    <div
      data-testid="scores-contacts-pending"
      className="flex items-center gap-2 rounded-md border border-border px-4 py-6 text-body text-text-secondary"
    >
      <Spinner className="h-4 w-4" />
      Loading the scored candidates…
    </div>
  );
}

function ContactsFailed({ onRetry }: { onRetry?: () => void }) {
  return (
    <div
      data-testid="scores-contacts-failed"
      className="flex flex-wrap items-center gap-3 rounded-md border border-border px-4 py-6 text-body text-text-secondary"
    >
      <span>
        The candidates were scored, but their details could not be loaded.
      </span>
      {onRetry ? (
        <Button variant="secondary" onClick={onRetry} data-testid="scores-contacts-retry">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Try again
        </Button>
      ) : null}
    </div>
  );
}

function ContactsVanished() {
  return (
    <div
      data-testid="scores-contacts-vanished"
      className="rounded-md border border-border px-4 py-6 text-body text-text-secondary"
    >
      The scored candidates are no longer in this people list.
    </div>
  );
}

function NotRunPlaceholder() {
  return (
    <div
      data-testid="scores-not-run"
      className="rounded-md border border-border bg-surface px-4 py-10 text-center text-body text-text-muted"
    >
      No results yet — run a "Preview sample" or "Review candidates" to score candidates.
    </div>
  );
}

/** How long a run may sit in `pending` before the page says it is queued. A
 *  normal pickup takes one or two 2.5 s polls. */
const QUEUED_NOTICE_AFTER_MS = 20_000;

/**
 * True once `run` has been `pending` for `ms` without moving. A timer rather
 * than a timestamp compared on render: the poll can return an identical
 * snapshot that TanStack's structural sharing turns into NO re-render, and the
 * run's `created_at` is not on the wire.
 */
function useQueuedLongerThan(run: AiReviewResult | undefined, ms: number): boolean {
  const [late, setLate] = useState(false);
  const pending = run?.status === "pending";
  const jobId = run?.job_id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `jobId` re-arms the timer for a new run
  useEffect(() => {
    setLate(false);
    if (!pending) return;
    const timer = setTimeout(() => setLate(true), ms);
    return () => clearTimeout(timer);
  }, [pending, jobId, ms]);
  return late;
}

function RunningOverlay({
  run,
  totalCandidates,
}: {
  run: AiReviewResult | undefined;
  totalCandidates: number | null;
}) {
  // The AI provider is down and the worker is waiting to retry (2026-09-29).
  // Said under the bar rather than instead of it: the count is still true.
  // This overlay is also the PREVIEW's only in-flight surface, and that is
  // where it matters most: every one of the 29 runs that failed on 2026-09-29
  // was a "Preview sample (5)", and a Retry there starts a NEW run rather than
  // resuming the old one. A preview still QUEUED gets its own line for the
  // same reason (/pr-review #981 iter 1) — but only once it has been queued
  // longer than a normal pickup (iter 2), or every healthy preview would
  // briefly claim to be waiting for a slot.
  const queuedLong = useQueuedLongerThan(run, QUEUED_NOTICE_AFTER_MS);
  const waiting =
    run?.status === "processing"
      ? aiReviewWaitingText(run.error)
      : queuedLong
        ? aiReviewQueuedText(run)
        : null;
  return (
    <div
      data-testid="scores-running"
      className="flex flex-col items-center gap-3 rounded-md border border-border bg-surface px-4 py-10"
    >
      {run?.progress ? (
        <>
          <ProgressBar
            value={run.progress.scored}
            max={run.progress.total}
            label="Scoring candidates"
            className="max-w-sm"
          />
          <p className="text-body text-text-secondary" data-testid="scores-progress-count">
            {run.progress.scored} / {run.progress.total} scored
          </p>
        </>
      ) : (
        (() => {
          // Before the backend populates `progress` (the pending window), show
          // how many candidates THIS run scores: a preview run carries
          // `sample_size` (e.g. 5); a full run has it null → fall back to the
          // whole-import total. Without this the pending phase always showed the
          // import total, even for a 5-candidate preview.
          const count = run?.sample_size ?? totalCandidates ?? 0;
          return (
            <>
              <ProgressBar indeterminate live label="Scoring candidates" className="max-w-sm" />
              <p className="text-body text-text-secondary">
                Scoring {count} candidate{count === 1 ? "" : "s"}…
              </p>
            </>
          );
        })()
      )}
      {waiting && (
        <p
          className="max-w-sm text-center text-body-sm text-warning-ink"
          data-testid="scores-provider-waiting"
        >
          {waiting}
        </p>
      )}
    </div>
  );
}

function FailedBanner({ run, onRetry }: { run: AiReviewResult | undefined; onRetry: () => void }) {
  // Which stage died (scoring vs building the file) is decided in exactly one
  // place — `aiReviewFailure.ts`. Nothing here reads `error.stage`.
  return (
    <div
      data-testid="scores-failed-banner"
      className="flex items-center justify-between gap-4 rounded-md border border-error-strong bg-error-muted px-4 py-3 text-body text-error"
    >
      <span>{aiReviewFailureText(run?.error)}</span>
      <Button variant="danger" onClick={onRetry} data-testid="scores-retry">
        Retry
      </Button>
    </div>
  );
}

/**
 * The preview's results table.
 *
 * This is `ContactsTable` — the SAME component the Import Detail page renders,
 * not a lookalike (Trello #729: "this should always be the same view"). It used
 * to be a private table in this file, and the difference that mattered most was
 * the rationale: here it was a hover-only `Tooltip` with no height bound and no
 * keyboard or touch path to it, while the detail page rendered it in-cell. Now
 * there is one implementation and one answer.
 *
 * The rows arrive already joined (`previewRows.joinScores`) because a run item
 * carries only an id, a name and its scores — the avatar, headline and current
 * position come from the contacts endpoint, fetched by `AiReviewPage`.
 *
 * `variant="preview"` drops the columns that would describe things that have
 * not happened yet (export, Thrive id, enrichment) plus selection and the
 * filter bars; see the prop's own note in `ContactsTable`.
 */
function ResultsTable({
  rows,
  columns,
  rationaleFor,
  resolvedAvatars,
  importId,
  isBct,
  sort,
  onSort,
  onOpenPerson,
  openPersonHandle,
  openPersonRowId,
}: {
  rows: ImportContactRow[];
  columns: AnnexColumn[];
  rationaleFor?: (contactId: string, criterionKey: string) => string | undefined;
  resolvedAvatars?: ReadonlyMap<string, string>;
  importId?: string;
  isBct?: boolean;
  sort?: ScoresTableSort | null;
  onSort?: (key: string) => void;
  onOpenPerson?: (row: ImportContactRow, handle: string) => void;
  openPersonHandle?: string | null;
  openPersonRowId?: string | null;
}) {
  return (
    <div data-testid="scores-results-table">
      {onSort && (
        <div
          aria-label="Sort results"
          className="flex items-center gap-2 rounded-t border border-b-0 border-border bg-surface px-3 py-2 text-label text-text-secondary"
        >
          <span className="font-semibold">Sort by:</span>
          {(["name", "weighted_total"] as const).map((key) => {
            const active = sort?.key === key;
            const dir = sort?.dir ?? "asc";
            const label = key === "name" ? "Candidate" : "Total";
            const dirLabel = dir === "asc" ? "ascending" : "descending";
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                aria-label={active ? `Sort by ${label}, ${dirLabel}` : `Sort by ${label}`}
                className={`inline-flex items-center gap-1 rounded px-2 py-1 cursor-pointer select-none hover:text-text${active ? " text-accent font-semibold" : ""}`}
                onClick={() => onSort(key)}
              >
                {label}
                {active ? (
                  dir === "asc"
                    ? <ArrowUp aria-hidden="true" className="h-3 w-3 text-accent" />
                    : <ArrowDown aria-hidden="true" className="h-3 w-3 text-accent" />
                ) : (
                  <ArrowUpDown aria-hidden="true" className="h-3 w-3 text-text-muted" />
                )}
              </button>
            );
          })}
        </div>
      )}
      <ContactsTable
        variant="preview"
        contacts={rows}
        columns={columns}
        rationaleFor={rationaleFor}
        resolvedAvatars={resolvedAvatars}
        // Entry 0 of `ContactsTable`'s width-measurement signature. What it
        // buys is INTRA-mount: this page swaps `selected` without unmounting
        // the table, so without it a criterion would keep the width measured
        // for the PREVIOUS import. It does NOT share a measurement with the
        // detail page — `measuredFor` is a `useRef`, so every mount measures
        // once from scratch whatever the id is. (Widths the user DRAGS are
        // shared across both surfaces, but by `useColumnWidths`'s
        // `contacts:<columnKey>` namespace, which carries no importId at all.)
        importId={importId}
        isBct={isBct}
        onOpenPerson={onOpenPerson}
        openPersonHandle={openPersonHandle}
        openPersonRowId={openPersonRowId}
      />
    </div>
  );
}

export function ScoresTable({
  run,
  isPolling,
  stale,
  totalCandidates,
  onPreview,
  sampleSize,
  previewDisabled,
  previewDisabledHint,
  onApplyAll,
  applyDisabled,
  applyDisabledHint,
  applying,
  previewPending = false,
  abandoned = false,
  rows = [],
  columns = [],
  rationaleFor,
  resolvedAvatars,
  importId,
  isBct,
  contactsPending = false,
  contactsFailed = false,
  scoredCount,
  onRetryContacts,
  sort,
  onSort,
  onOpenPerson,
  openPersonHandle,
  openPersonRowId,
}: ScoresTableProps) {
  const failed = run?.status === "failed";
  // The second async hop: scoring is done, the derived file is being built.
  const building = run?.status === "applying";
  // succeeded, applied AND applying all carry per-contact items — keep the
  // table rendered throughout (otherwise it flips to the "no results"
  // placeholder beside the applied label, or to the scoring overlay mid-build).
  const hasResults =
    (run?.status === "succeeded" || run?.status === "applied" || building) &&
    (run.items?.length ?? 0) > 0;
  // A preview run: scored only a random subset, cannot be applied.
  const isSample = run?.sample_size != null;
  // The Preview + Apply buttons are both busy while a run is in flight or the
  // apply mutation is running.
  //
  // The `stalledBuild` override that used to live here is gone with the rest
  // of the browser-driven apply. It answered "the build blew past its deadline,
  // so stop calling it in-flight and re-enable the retry" — and there is no
  // browser-side retry left to re-enable. A row whose build strands is settled
  // `failed` by the ai-review sweeper, which the poll then reports here.
  // `abandoned` reaches BOTH derived flags, not just the label. It reached only
  // `applyingNow` in iter 2, which left the `applying`-stranded case with
  // `busy` still true from `applying` — Preview disabled by `busy`, Apply
  // disabled by `abandoned`, and therefore ZERO enabled actions on a page
  // telling the recruiter to do something (`/pr-review` iter 3, Agent C V2).
  //
  // Preview comes back (it is cheap and creates no file, so it is the one
  // action that is safe here); Apply stays disabled below, because the run may
  // still be building server-side and a second click is a second paid run.
  const busy = !abandoned && (isPolling || applying || previewPending);
  // The gap between scoring finishing and the apply worker claiming the run.
  //
  // For a FULL run `succeeded` is a hop, not a destination — the scoring worker
  // auto-applies it (`processing -> succeeded -> applying -> applied`). Without
  // this the apply button dropped back to its idle label "Review candidates"
  // mid-run: disabled, but reading as though the work were finished.
  //
  // `isRunInFlight` is IMPORTED, not re-derived. This component kept its own
  // copy of the predicate for two rounds and that is precisely how the page
  // and the table came to disagree about whether a run was still going —
  // `/pr-review` recommended exporting one predicate in iter 1 and the same
  // class of bug recurred in iters 2 and 3.
  //
  // Precisely: one definition of LIVENESS, three readers. `applying` and
  // `building` stay independent — they answer "which hop", not "is it alive" —
  // so this is not a claim that every in-flight notion in this file is shared
  // (`/pr-review` iter 4, Agent C). And sharing the predicate did not prevent
  // iter 4's bug, which was a consumer reading the right value with the wrong
  // intent after `isPolling` changed meaning.
  const autoApplyPending = isRunInFlight(run) && run?.status === "succeeded";
  const applyingNow = !abandoned && (applying || autoApplyPending);
  // "Review candidates" is only enabled once a preview has been run for the
  // CURRENT criteria (a fresh succeeded run). Editing criteria (stale) or
  // never previewing keeps it disabled — you preview, then create.
  const previewReady = run?.status === "succeeded" && !stale;

  let body: ReactNode;
  if (totalCandidates === 0) {
    body = <EmptyState />;
  } else if (busy && !building) {
    // `busy`, not `isPolling`: past the deadline the page is still polling but
    // has told the recruiter it stopped waiting, and a "Scoring N candidates…"
    // overlay under that banner contradicts it (`/pr-review` iter 4).
    //
    // `building` is non-terminal too, but it is NOT scoring — showing the
    // overlay there would blank the recruiter's scores and misdescribe what the
    // server is doing.
    body = (
      <RunningOverlay run={run} totalCandidates={previewPending ? sampleSize : totalCandidates} />
    );
  } else if (failed) {
    body = <FailedBanner run={run} onRetry={onPreview} />;
  } else if (hasResults && run && contactsPending && rows.length === 0) {
    // Scores are in, their contacts are not yet. Two endpoints answer for one
    // table, and only this branch stops the gap between them from rendering as
    // an empty result for a run that just succeeded.
    //
    // ABOVE the failed branch, deliberately. `contactsPending` is
    // `isLoading || isFetching`, and a REFETCH of an errored query keeps
    // `status: "error"` while only `fetchStatus` flips — so with failed first,
    // "Try again" produced no visible change at all until the request settled,
    // which is the second impatient click it was meant to prevent.
    body = <ContactsPending />;
  } else if (hasResults && run && contactsFailed && rows.length === 0) {
    // The scoring SUCCEEDED and its contacts did not load. Saying "no
    // candidates" here would blame the run for a fetch failure.
    //
    // An applied run cannot reach here, and no longer needs a branch above it
    // to say so: `scoreOnlyRows` fills `rows` from the run itself, so
    // `rows.length === 0` is false. (Until `3b5a9199` an explicit applied
    // branch sat above this one; the guarantee now comes from where the rows
    // are built rather than from ordering.)
    body = <ContactsFailed onRetry={onRetryContacts} />;
  } else if (hasResults && run && rows.length === 0) {
    // The fetch SUCCEEDED and still joined nothing: every scored contact is
    // gone from the parent list (deleted between the run and this fetch — the
    // case `joinScores` drops on purpose). The table's own empty state would
    // say "This run scored no candidates.", which is false about a run that
    // scored several. Name the actual cause instead.
    body = <ContactsVanished />;
  } else if (hasResults && run) {
    body = (
      <ResultsTable
        rows={rows}
        columns={columns}
        rationaleFor={rationaleFor}
        resolvedAvatars={resolvedAvatars}
        importId={importId ?? undefined}
        isBct={isBct}
        sort={sort}
        onSort={onSort}
        onOpenPerson={onOpenPerson}
        openPersonHandle={openPersonHandle}
        openPersonRowId={openPersonRowId}
      />
    );
  } else {
    body = <NotRunPlaceholder />;
  }

  return (
    <div className="flex flex-col gap-3" data-testid="scores-table">
      {/* The Preview action sits on the LEFT, above the table — it is what
          populates it (a cheap, non-persisting look at a random sample). Kept
          ACTIVE (only disabled while a run is in flight) so it never greys out;
          `handlePreviewScores` no-ops if the config isn't ready and the hint
          explains what's missing. */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          onClick={onPreview}
          disabled={busy}
          data-testid="preview-sample-button"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Preview sample ({sampleSize})
        </Button>
        <h2 className="text-h2 text-text">Candidate scores</h2>
        {previewDisabled && previewDisabledHint ? (
          <span className="text-caption text-text-muted" data-testid="preview-disabled-hint">
            {previewDisabledHint}
          </span>
        ) : null}
      </div>

      {/* stale dims the TABLE, never the actions. */}
      <div data-stale={stale ? "true" : "false"} className={stale ? "opacity-50" : undefined}>
        {body}
      </div>

      <ScoreLegend />

      {/* Fewer rows on screen than the run scored — for TWO unrelated reasons,
          which an earlier version of this banner collapsed into one sentence
          and so could describe the wrong cause:

            1. the client truncated the id list to the endpoint's ceiling
               (`MAX_PREVIEW_IDS`), which is about the request; and
            2. `joinScores` dropped rows whose contacts no longer exist in the
               parent list, which is about the data.

          Both can be true at once, so they are computed independently and
          rendered as separate sentences rather than one summed difference. */}
      {(() => {
        // `!busy` matters as much as `hasResults`: a full run is `succeeded`
        // (so `hasResults`) while `autoApplyPending` keeps `busy` true, and the
        // body is then `RunningOverlay` ("Scoring 1,240 candidates…"). Without
        // this the banner sat beneath it describing a preview table that was
        // not on screen and, for a full run, never would be.
        // `busy && !building`, mirroring the BODY's condition at :395 — not a
        // bare `busy`. `isPolling` stays true through the whole `applying` hop,
        // so a bare `busy` suppressed this note in the ONE state where a
        // truncated table is on screen: the legacy arm keeps the page mounted,
        // a 1,240-contact full run reaches `applying`, the body falls through
        // to `ResultsTable` with 25 rows, and nothing said the other 1,215 were
        // never requested. That is the silent truncation this banner exists
        // for, hidden by its own guard.
        if (typeof scoredCount !== "number" || !hasResults) return null;
        if (busy && !building) return null;
        // Silent on an applied run: its fetch returns nothing by design, so
        // "the preview loads 25 of them" would be false — it loads 0, and the
        // body beside it already says the scores have moved.
        if (run?.status === "applied") return null;
        const requested = Math.min(scoredCount, MAX_PREVIEW_IDS);
        const truncated = scoredCount > MAX_PREVIEW_IDS;
        // Of the rows we actually ASKED for, how many failed to come back.
        //
        // Suppressed entirely at `rows.length === 0`, where `ContactsVanished`
        // is the body and already says it — but the TRUNCATION half is not,
        // because nothing else can. A 1,240-contact run that returns no rows
        // otherwise reads as "the scored candidates are no longer in this
        // people list", which silently drops the 1,215 that were never asked
        // for. That state was a hole in the first version of this banner.
        const missing = rows.length === 0 ? 0 : Math.max(requested - rows.length, 0);
        if (!truncated && missing === 0) return null;
        return (
          <div
            data-testid="scores-truncated-banner"
            className="flex flex-col gap-1 rounded-md border border-warning bg-surface px-4 py-3 text-body text-text-secondary"
          >
            {truncated ? (
              <span data-testid="scores-truncated-note">
                {/* Phrased about the REQUEST, not about what is on screen.
                    This note survives `rows.length === 0` (so a run whose
                    contacts all vanished still reports the ones it never asked
                    for), and it renders alongside the pending and failed
                    bodies too — in all three of those states nothing is
                    displayed, so "Showing 25 of 1,240" would be false. "Loads
                    the first 25" is true whether or not they arrived.

                    NOT "the top 25": `run.items` arrives ordered by
                    `(last_name, first_name, id)` (the AI-review repository's
                    `order_by`) and `joinScores` preserves it, so the cut is
                    stable and alphabetical — but a recruiter reads "the first
                    25" as the best-scoring, which is the one thing it is not.
                    Naming the ordering removes the implicature.

                    Locale PINNED to en-US: a test asserting "1,240" must not
                    depend on the runner's locale (de-DE emits "1.240"). */}
                This run scored {scoredCount.toLocaleString("en-US")} candidates;
                the preview loads {requested.toLocaleString("en-US")} of them
                (alphabetical by surname).
              </span>
            ) : null}
            {missing > 0 ? (
              <span data-testid="scores-missing-note">
                {missing} scored {missing === 1 ? "candidate is" : "candidates are"} no
                longer in this people list.
              </span>
            ) : null}
          </div>
        );
      })()}

      {isSample && hasResults ? (
        <div
          data-testid="scores-sample-banner"
          className="rounded-md border border-border bg-surface px-4 py-3 text-body text-text-secondary"
        >
          Preview of {sampleSize} random {sampleSize === 1 ? "candidate" : "candidates"} — click "Review candidates"
          to score every contact and save them to a new people list.
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        {/* Apply = the primary action: score EVERY contact and save them to a
            NEW file (the caller confirms first, then runs full + auto-applies).
            Enabled ONLY after a preview has been run; once the file exists, a
            static label replaces the button. */}
        {run?.status === "applied" && !stale ? (
          <span className="text-body text-text-secondary" data-testid="scores-applied-label">
            Candidates reviewed ✓
          </span>
        ) : (
          <Button
            onClick={onApplyAll}
            // `abandoned` DISABLES rather than re-enables. The run may still be
            // building server-side; a second click would start a second paid
            // Bedrock run over the same contacts. The banner above tells the
            // recruiter where the file actually is.
            disabled={applyDisabled || busy || !previewReady || abandoned}
            data-testid="apply-all-button"
          >
            {applyingNow ? <Spinner className="h-4 w-4 mr-2" /> : null}
            {/* "Scoring…" only while the AI is actually scoring. Both of the
                other two in-flight states are about the FILE: `building` is the
                apply worker writing it, and `autoApplyPending` is the moment
                between scoring finishing and that worker picking the run up.
                Reading "Scoring…" there would restate work already done. */}
            {applyingNow
              ? building || autoApplyPending
                ? "Saving…"
                : "Scoring…"
              : "Review candidates"}
          </Button>
        )}
        {stale ? (
          <span className="text-caption text-warning" data-testid="stale-note">
            Criteria changed — click Preview to refresh
          </span>
        ) : applyDisabledHint ? (
          <span className="text-caption text-text-muted" data-testid="apply-disabled-hint">
            {applyDisabledHint}
          </span>
        ) : !applyDisabled && !busy && !previewReady && run?.status !== "applied" ? (
          <span className="text-caption text-text-muted" data-testid="apply-needs-preview-hint">
            Run a preview first
          </span>
        ) : null}
      </div>
    </div>
  );
}
